import { createContext, useCallback, useContext, ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { queryKeys, STALE_TIMES } from '@/lib/queryKeys';

export interface UserStreak {
  currentStreak: number;
  longestStreak: number;
  totalXp: number;
  level: number;
  weeklyGoal: number;
  weeklyProgress: number;
  lastActiveDate: string | null;
}

const XP_PER_LEVEL = 100;

export const getLevelProgress = (totalXp: number): number => {
  return (totalXp % XP_PER_LEVEL) / XP_PER_LEVEL * 100;
};

export const getXpToNextLevel = (totalXp: number): number => {
  return XP_PER_LEVEL - (totalXp % XP_PER_LEVEL);
};

interface UserStreakContextType {
  streak: UserStreak | null;
  loading: boolean;
  refreshStreak: () => void;
  levelProgress: number;
  xpToNextLevel: number;
}

const UserStreakContext = createContext<UserStreakContextType>({
  streak: null,
  loading: true,
  refreshStreak: () => {},
  levelProgress: 0,
  xpToNextLevel: XP_PER_LEVEL,
});

export const useUserStreakContext = () => useContext(UserStreakContext);

const mapRow = (row: any): UserStreak => ({
  currentStreak: row.current_streak,
  longestStreak: row.longest_streak,
  totalXp: row.total_xp,
  level: row.level,
  weeklyGoal: row.weekly_goal,
  weeklyProgress: row.weekly_progress,
  lastActiveDate: row.last_active_date,
});

/**
 * Streak data is consumed by StreakCounter, GamificationNavBadge,
 * Achievements, and a handful of gamification widgets — often within
 * the same render. Backing the context with React Query (shared key +
 * staleTime) ensures one network round-trip per user across all callers
 * and tolerates brief staleness without refetching on every mount.
 */
export const UserStreakProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const queryKey = queryKeys.userStreak(user?.id);

  const { data: streak = null, isLoading } = useQuery<UserStreak | null>({
    queryKey,
    enabled: !!user,
    staleTime: STALE_TIMES.userStreak,
    gcTime: STALE_TIMES.userStreak * 5,
    queryFn: async () => {
      if (!user) return null;

      const { data, error } = await supabase
        .from('user_streaks')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching streak:', error);
      }

      if (data) return mapRow(data);

      // Auto-provision a streak row for first-time users.
      const { data: newData, error: insertError } = await supabase
        .from('user_streaks')
        .insert({ user_id: user.id })
        .select()
        .single();

      if (insertError || !newData) {
        if (insertError) console.error('Streak insert error:', insertError);
        return null;
      }

      return mapRow(newData);
    },
  });

  const refreshStreak = useCallback(() => {
    queryClient.invalidateQueries({ queryKey });
  }, [queryClient, queryKey]);

  return (
    <UserStreakContext.Provider value={{
      streak,
      loading: isLoading,
      refreshStreak,
      levelProgress: streak ? getLevelProgress(streak.totalXp) : 0,
      xpToNextLevel: streak ? getXpToNextLevel(streak.totalXp) : XP_PER_LEVEL,
    }}>
      {children}
    </UserStreakContext.Provider>
  );
};
